import {
  GiftOutlined,
  LineChartOutlined,
  HomeOutlined,
  UserOutlined,
  AppstoreOutlined
} from "@ant-design/icons";
import { Image, Layout, Menu } from "antd";
import { ArrowLeftIcon } from "components/svgs/ArrowLeftIcon";
import { ArrowRightIcon } from "components/svgs/ArrowRightIcon";
import React, { useState, useCallback, useContext, useMemo, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { GroupIcon, OrderIcon, HistoryIcon } from "../../../components/svgs";
import "./ClientSiderContent.scss";
import { ReactSVG } from "react-svg";
import { ProfileContext } from "layouts/private/ProfileContext";
import { Link } from "react-router-dom";
import UserInfo from "../Users/UserInfo";
import { BarChartIcon } from "components/svgs/menu/BarChartIcon";
import { RoutesIcon } from "components/svgs/menu/RoutesIcon";
import { LocationsIcon } from "components/svgs/menu/LocationsIcon";
import { DriversIcon } from "components/svgs/menu/DriversIcon";
import { NeedHelpIcon } from "components/svgs/menu/NeedHelpIcon";
import { InvoicingIcon } from "components/svgs/menu/InvoicingIcon";
import { SettingIcon } from "components/svgs/menu/SettingIcon";
import companySettingsApi from "../../../modules/client/settings/api/companySettingsApi";
import request from "helper/request";
import { ENTITLEMENT_IDS } from "../../../configs/entitlements";

const { Sider } = Layout;
const { SubMenu } = Menu;
const getPathName = (str) => {
  let rs = "/";
  const path = str && str[str.length - 1] == "/" ? str.substring(0, str.length - 1) : str;

  // Handle warehouse routes - all should highlight the warehouses menu item
  if (path.startsWith("/warehouses")) {
    return "/warehouses";
  }

  // Handle purchase order routes
  if (path.startsWith("/purchaseorders")) {
    return "/purchaseorders";
  }

  // Handle routes detail pages - all should highlight the routes menu item
  if (path.startsWith("/routes/")) {
    return "/routes";
  }

  // Handle plans pages
  if (path.startsWith("/plans")) {
    return "/plans";
  }

  // Handle scan2pack pages
  if (path.startsWith("/scan2pack")) {
    return "/scan2pack";
  }

  // Handle saved routes pages
  if (path.startsWith("/saved-routes")) {
    return "/saved-routes";
  }

  switch (path) {
    case "/home":
      rs = "/home";
      break;
    case "/dashboard":
      rs = "/dashboard";
      break;
    case "/reports/sftp-pod":
      rs = "/reports/sftp-pod";
      break;
    case "/route/new":
    case "/orders/new":
      rs = "/orders/new";
      break;
    case "/delivery-plan":
      rs = "/delivery-plan";
      break;
    case "/routes":
      rs = "/routes";
      break;
    case "/pool":
      rs = "/pool";
      break;
    case "/locations":
      rs = "/locations";
      break;
    case "/history":
      rs = "/history";
      break;
    case "/client-users":
      rs = "/client-users";
      break;
    case "/drivers":
      rs = "/drivers";
      break;
    case "/billing/dashboard":
      rs = "/billing/dashboard";
      break;
    case "/billing/plans":
      rs = "/billing/plans";
      break;
    case "/billing/facturas":
      rs = "/billing/facturas";
      break;
    case "/billing/pool-invoices":
      rs = "/billing/pool-invoices";
      break;
    case "/billing/invoicing":
      rs = "/billing/invoicing";
      break;
    case "/billing/subscriptions":
      rs = "/billing/subscriptions";
      break;
    case "/billing/payment-method":
      rs = "/billing/payment-method";
      break;
    case "/billing/upgrades":
      rs = "/billing/upgrades";
      break;
    case "/settings/my-profile":
      rs = "/settings/my-profile";
      break;
    case "/settings/notification":
      rs = "/settings/notification";
      break;
    case "/settings/validations":
      rs = "/settings/validations";
      break;
    case "/settings/route-options":
      rs = "/settings/route-options";
      break;
    case "/settings/change-password":
      rs = "/settings/change-password";
      break;
    case "/settings/company-settings":
      rs = "/settings/company-settings";
      break;
    case "/api-key":
      rs = "/api-key";
      break;
    case "/ai":
      break;
    case "/ai-import":
      rs = "/ai-import";
      break;
    case "/import-file":
      rs = "/import-file";
      break;
  }
  return rs;
};

const ClientSiderContent = () => {
  const location = useLocation();
  const pathname = getPathName(getPathName(location.pathname));
  const [siderKey, setSiderKey] = useState(pathname);

  // Initialize openKey based on current path
  const getInitialOpenKeys = () => {
    if (location.pathname.startsWith("/billing/")) {
      return ["billing"];
    }
    if (location.pathname.startsWith("/settings/")) {
      return ["settings"];
    }
    if (
      location.pathname.startsWith("/warehouses") ||
      location.pathname.startsWith("/purchaseorders")
    ) {
      return ["inventory"];
    }
    if (
      location.pathname.startsWith("/routes") ||
      location.pathname.startsWith("/plans") ||
      location.pathname.startsWith("/saved-routes")
    ) {
      return ["routes-menu"];
    }
    if (location.pathname.startsWith("/scan2pack")) {
      return [];
    }
    if (
      location.pathname.startsWith("/dashboard") ||
      location.pathname.startsWith("/reports/")
    ) {
      return ["dashboard-menu"];
    }
    if (
      location.pathname.startsWith("/delivery-plan") ||
      location.pathname.startsWith("/ai-import") ||
      location.pathname.startsWith("/import-file") ||
      location.pathname.startsWith("/orders/new") ||
      location.pathname.startsWith("/pool-orders/new")
    ) {
      return ["delivery-plan-menu"];
    }
    return [];
  };

  const [openKey, setOpenKey] = useState(getInitialOpenKeys);
  const [collapsed, setCollapsed] = useState(false);
  const profileCtx = useContext(ProfileContext);
  const profile = useMemo(() => profileCtx.profile, [profileCtx.profile]);

  // Client logo state
  const [clientLogo, setClientLogo] = useState(null);
  const [shouldShowClientLogo, setShouldShowClientLogo] = useState(false);

  // Warehouse entitlement state
  const [hasWarehouseAccess, setHasWarehouseAccess] = useState(false);

  // Purchase Order entitlement state
  const [hasPurchaseOrderAccess, setHasPurchaseOrderAccess] = useState(false);

  // AI Import entitlement state
  const [hasAIImportAccess, setHasAIImportAccess] = useState(false);

  // Pool orders existence state
  const [hasPoolOrders, setHasPoolOrders] = useState(false);

  // Load client logo and entitlement data
  const loadClientLogo = useCallback(async () => {
    try {
      // Reset state first
      setClientLogo(null);
      setShouldShowClientLogo(false);

      // Check if client has logo uploaded
      const logoData = await companySettingsApi.getLogo();
      if (!logoData || !logoData.url) {
        return; // No logo uploaded
      }

      // Check if client has white label entitlement
      const entitlementData = await companySettingsApi.getEntitlement(
        ENTITLEMENT_IDS.WHITE_LABEL
      );
      if (!entitlementData.hasEntitlement || !entitlementData.isActive) {
        return; // No entitlement or not active
      }

      // Check if showLogo setting is enabled
      const showLogo = entitlementData.settings?.showLogo === "1";
      if (!showLogo) {
        return; // Setting disabled
      }

      // All conditions met - show client logo
      setClientLogo(logoData.url);
      setShouldShowClientLogo(true);
    } catch (error) {
      console.log("Could not load client logo:", error);
      // Fallback to Flexio logo - no action needed
    }
  }, []);

  useEffect(() => {
    loadClientLogo();

    // Listen for custom events to refresh logo (e.g., when settings are updated)
    const handleLogoUpdate = (event) => {
      console.log("Logo update event received:", event.type, event.detail);
      loadClientLogo();
    };

    window.addEventListener("companyLogoUpdated", handleLogoUpdate);
    window.addEventListener("whiteLabelSettingsUpdated", handleLogoUpdate);
    window.addEventListener("entitlementStatusChanged", handleLogoUpdate);

    return () => {
      window.removeEventListener("companyLogoUpdated", handleLogoUpdate);
      window.removeEventListener("whiteLabelSettingsUpdated", handleLogoUpdate);
      window.removeEventListener("entitlementStatusChanged", handleLogoUpdate);
    };
  }, [loadClientLogo]);

  const loadWarehouseEntitlement = useCallback(async () => {
    console.log("🏭 [Warehouse] Checking entitlement...", ENTITLEMENT_IDS.WAREHOUSE);
    try {
      const entitlementData = await companySettingsApi.getEntitlement(
        ENTITLEMENT_IDS.WAREHOUSE
      );

      console.log("🏭 [Warehouse] API Response:", {
        hasEntitlement: entitlementData?.hasEntitlement,
        isActive: entitlementData?.isActive,
        fullData: entitlementData
      });

      const hasAccess =
        entitlementData?.hasEntitlement === true && entitlementData?.isActive === true;

      if (hasAccess) {
        console.log("✅ [Warehouse] Access GRANTED - Tab will be visible");
      } else {
        console.log("❌ [Warehouse] Access DENIED - Tab will be hidden", {
          reason: !entitlementData?.hasEntitlement
            ? "No entitlement"
            : "Entitlement not active"
        });
      }

      setHasWarehouseAccess(hasAccess);
    } catch (error) {
      console.error("❌ [Warehouse] Error loading entitlement:", error);
      setHasWarehouseAccess(false);
    }
  }, []);

  useEffect(() => {
    loadWarehouseEntitlement();

    // Listen for entitlement changes
    const handleEntitlementUpdate = () => {
      loadWarehouseEntitlement();
    };

    window.addEventListener("entitlementStatusChanged", handleEntitlementUpdate);

    return () => {
      window.removeEventListener("entitlementStatusChanged", handleEntitlementUpdate);
    };
  }, [loadWarehouseEntitlement]);

  const loadPurchaseOrderEntitlement = useCallback(async () => {
    console.log(
      "📦 [PurchaseOrder] Checking entitlement...",
      ENTITLEMENT_IDS.PURCHASE_ORDER
    );
    try {
      const entitlementData = await companySettingsApi.getEntitlement(
        ENTITLEMENT_IDS.PURCHASE_ORDER
      );

      console.log("📦 [PurchaseOrder] API Response:", {
        hasEntitlement: entitlementData?.hasEntitlement,
        isActive: entitlementData?.isActive,
        fullData: entitlementData
      });

      const hasAccess =
        entitlementData?.hasEntitlement === true && entitlementData?.isActive === true;

      if (hasAccess) {
        console.log("✅ [PurchaseOrder] Access GRANTED - Tab will be visible");
      } else {
        console.log("❌ [PurchaseOrder] Access DENIED - Tab will be hidden", {
          reason: !entitlementData?.hasEntitlement
            ? "No entitlement"
            : "Entitlement not active"
        });
      }

      setHasPurchaseOrderAccess(hasAccess);
    } catch (error) {
      console.error("❌ [PurchaseOrder] Error loading entitlement:", error);
      setHasPurchaseOrderAccess(false);
    }
  }, []);

  useEffect(() => {
    loadPurchaseOrderEntitlement();

    // Listen for entitlement changes
    const handleEntitlementUpdate = () => {
      loadPurchaseOrderEntitlement();
    };

    window.addEventListener("entitlementStatusChanged", handleEntitlementUpdate);

    return () => {
      window.removeEventListener("entitlementStatusChanged", handleEntitlementUpdate);
    };
  }, [loadPurchaseOrderEntitlement]);

  const loadAIImportEntitlement = useCallback(async () => {
    console.log("🤖 [AIImport] Checking entitlement...", ENTITLEMENT_IDS.AI_IMPORT);
    try {
      const entitlementData = await companySettingsApi.getEntitlement(
        ENTITLEMENT_IDS.AI_IMPORT
      );

      console.log("🤖 [AIImport] API Response:", {
        hasEntitlement: entitlementData?.hasEntitlement,
        isActive: entitlementData?.isActive,
        fullData: entitlementData
      });

      const hasAccess =
        entitlementData?.hasEntitlement === true && entitlementData?.isActive === true;

      if (hasAccess) {
        console.log("✅ [AIImport] Access GRANTED - Tab will be visible");
      } else {
        console.log("❌ [AIImport] Access DENIED - Tab will be hidden", {
          reason: !entitlementData?.hasEntitlement
            ? "No entitlement"
            : "Entitlement not active"
        });
      }

      setHasAIImportAccess(hasAccess);
    } catch (error) {
      console.error("❌ [AIImport] Error loading entitlement:", error);
      setHasAIImportAccess(false);
    }
  }, []);

  useEffect(() => {
    loadAIImportEntitlement();

    // Listen for entitlement changes
    const handleEntitlementUpdate = () => {
      loadAIImportEntitlement();
    };

    window.addEventListener("entitlementStatusChanged", handleEntitlementUpdate);

    return () => {
      window.removeEventListener("entitlementStatusChanged", handleEntitlementUpdate);
    };
  }, [loadAIImportEntitlement]);

  const loadPoolOrdersExistence = useCallback(async () => {
    try {
      const response = await request.get("orderv2/all-packages", {
        params: { page: 1, pageSize: 20, isFlexioPoolOrder: true }
      });
      setHasPoolOrders((response?.metadata?.totalItems ?? 0) > 0);
    } catch {
      setHasPoolOrders(false);
    }
  }, []);

  useEffect(() => {
    loadPoolOrdersExistence();
  }, [loadPoolOrdersExistence]);

  // Update sidebar state when location changes (e.g., browser back/forward, direct URL)
  useEffect(() => {
    const newPathname = getPathName(location.pathname);
    setSiderKey(newPathname);

    if (location.pathname.startsWith("/billing/")) {
      setOpenKey(["billing"]);
    } else if (location.pathname.startsWith("/settings/")) {
      setOpenKey(["settings"]);
    } else if (
      location.pathname.startsWith("/warehouses") ||
      location.pathname.startsWith("/purchaseorders")
    ) {
      setOpenKey(["inventory"]);
    } else if (
      location.pathname.startsWith("/routes") ||
      location.pathname.startsWith("/plans") ||
      location.pathname.startsWith("/saved-routes")
    ) {
      setOpenKey(["routes-menu"]);
    } else if (location.pathname.startsWith("/scan2pack")) {
      setOpenKey([]);
    } else if (
      location.pathname.startsWith("/delivery-plan") ||
      location.pathname.startsWith("/ai-import") ||
      location.pathname.startsWith("/import-file") ||
      location.pathname.startsWith("/orders/new") ||
      location.pathname.startsWith("/pool-orders/new")
    ) {
      setOpenKey(["delivery-plan-menu"]);
    } else if (
      location.pathname.startsWith("/dashboard") ||
      location.pathname.startsWith("/reports/")
    ) {
      setOpenKey(["dashboard-menu"]);
    } else {
      // Only close submenu if we're not in a billing, settings, inventory, routes, or delivery-plan page
      setOpenKey([]);
    }
  }, [location.pathname]);

  const handleClickCollapse = () => {
    setCollapsed(!collapsed);
  };

  const history = useHistory();

  const handleClickMenuItem = useCallback(
    (value) => {
      const key = value?.key;
      // Skip navigation for the AI chat button
      if (key === "ai-chat") {
        return; // Don't do navigation
      }
      setSiderKey(getPathName(key));

      // Keep submenu open if navigating to any billing, settings, inventory, routes, or delivery-plan page
      if (key && key.startsWith("/billing/")) {
        setOpenKey(["billing"]);
      } else if (key && key.startsWith("/settings/")) {
        setOpenKey(["settings"]);
      } else if (
        key &&
        (key.startsWith("/warehouses") || key.startsWith("/purchaseorders"))
      ) {
        setOpenKey(["inventory"]);
      } else if (
        key &&
        (key.startsWith("/routes") ||
          key.startsWith("/plans") ||
          key.startsWith("/saved-routes"))
      ) {
        setOpenKey(["routes-menu"]);
      } else if (
        key &&
        (key.startsWith("/delivery-plan") ||
          key.startsWith("/ai-import") ||
          key.startsWith("/import-file") ||
          key.startsWith("/orders/new") ||
          key.startsWith("/pool-orders/new"))
      ) {
        setOpenKey(["delivery-plan-menu"]);
      } else if (key && (key.startsWith("/dashboard") || key.startsWith("/reports/"))) {
        setOpenKey(["dashboard-menu"]);
      } else {
        setOpenKey([]);
      }

      key && history.push(`${key}`);
    },
    [setSiderKey, setOpenKey, history]
  );

  const handleClickMenuSub = useCallback(
    (value) => {
      // Special handling for billing submenu - navigate to billing dashboard
      if (value.key === "billing") {
        setSiderKey("/billing/dashboard");
        history.push("/billing/dashboard");
        // Still open the submenu
        const newOpenKeys = [...openKey];
        if (!newOpenKeys.includes(value.key)) {
          newOpenKeys.push(value.key);
          setOpenKey(newOpenKeys);
        }
        return;
      }
      // Special handling for settings submenu - navigate to my profile
      if (value.key === "settings") {
        setSiderKey("/settings/my-profile");
        history.push("/settings/my-profile");
        // Still open the submenu
        const newOpenKeys = [...openKey];
        if (!newOpenKeys.includes(value.key)) {
          newOpenKeys.push(value.key);
          setOpenKey(newOpenKeys);
        }
        return;
      }
      // Special handling for inventory submenu - navigate to warehouses
      if (value.key === "inventory") {
        setSiderKey("/warehouses");
        history.push("/warehouses");
        // Still open the submenu
        const newOpenKeys = [...openKey];
        if (!newOpenKeys.includes(value.key)) {
          newOpenKeys.push(value.key);
          setOpenKey(newOpenKeys);
        }
        return;
      }
      // Special handling for routes-menu submenu - navigate to active routes
      if (value.key === "routes-menu") {
        setSiderKey("/routes");
        history.push("/routes");
        // Still open the submenu
        const newOpenKeys = [...openKey];
        if (!newOpenKeys.includes(value.key)) {
          newOpenKeys.push(value.key);
          setOpenKey(newOpenKeys);
        }
        return;
      }
      // Special handling for dashboard submenu - navigate to the dashboard
      if (value.key === "dashboard-menu") {
        setSiderKey("/dashboard");
        history.push("/dashboard");
        // Still open the submenu
        const newOpenKeys = [...openKey];
        if (!newOpenKeys.includes(value.key)) {
          newOpenKeys.push(value.key);
          setOpenKey(newOpenKeys);
        }
        return;
      }
      // Special handling for delivery-plan-menu submenu - navigate to delivery plan
      if (value.key === "delivery-plan-menu") {
        setSiderKey("/delivery-plan");
        history.push("/delivery-plan");
        // Still open the submenu
        const newOpenKeys = [...openKey];
        if (!newOpenKeys.includes(value.key)) {
          newOpenKeys.push(value.key);
          setOpenKey(newOpenKeys);
        }
        return;
      }
      const newOpenKeys = [...openKey];
      if (newOpenKeys.includes(value.key)) {
        const index = newOpenKeys.findIndex((key) => key === value.key);
        delete newOpenKeys[index];
        setOpenKey(newOpenKeys);
      } else {
        newOpenKeys.push(value.key);
        setOpenKey(newOpenKeys);
      }
    },
    [openKey, history]
  );

  const [isOpenSidebar, setIsOpenSidebar] = useState(false);
  const toggleSidebar = () => {
    setIsOpenSidebar(false);
  };

  return (
    <>
      <Sider
        className="client-sider-content-component"
        trigger={null}
        collapsible
        width={220}
        collapsed={collapsed}
      >
        <div className="sidebar-header">
          <Link to="/">
            <Image
              src={
                shouldShowClientLogo && clientLogo
                  ? clientLogo
                  : `/logos/${window.REACT_APP_LOGO_FILE_NAME}`
              }
              alt="logo"
              preview={false}
              style={{
                width: collapsed ? "2rem" : "8rem",
                maxWidth: "100%",
                maxHeight: collapsed ? "2rem" : "4rem",
                objectFit: "contain",
                transition: "all 0.2s ease"
              }}
            />
          </Link>
        </div>
        <div className="sidebar-menu">
          <Menu
            mode="inline"
            openKeys={openKey}
            selectedKeys={[siderKey]}
            onClick={handleClickMenuItem}
          >
            <Menu.Item
              key="/home"
              icon={
                <span className="anticon">
                  <HomeOutlined />
                </span>
              }
            >
              Home
            </Menu.Item>
            <SubMenu
              key="dashboard-menu"
              icon={
                <span className="anticon">
                  <BarChartIcon />
                </span>
              }
              title="Dashboard"
              onTitleClick={handleClickMenuSub}
            >
              <Menu.Item key="/dashboard">Dashboard</Menu.Item>
              <Menu.Item key="/reports/sftp-pod">SFTP Report</Menu.Item>
            </SubMenu>
            {window.RESELLER_FLAG && (
              <>
                {profile?.isReseller && (
                  <Menu.Item
                    key="/reseller/dashboard"
                    icon={
                      <span className="anticon">
                        <LineChartOutlined />
                      </span>
                    }
                  >
                    Reseller
                  </Menu.Item>
                )}
              </>
            )}
            <SubMenu
              key="delivery-plan-menu"
              icon={
                <span className="anticon">
                  <RoutesIcon />
                </span>
              }
              title="Plan Delivery"
              onTitleClick={handleClickMenuSub}
            >
              {hasAIImportAccess && <Menu.Item key="/ai-import">AI Import</Menu.Item>}
              <Menu.Item key="/import-file">Import File</Menu.Item>
              <Menu.Item key="/orders/new">Manual Route</Menu.Item>
              <Menu.Item key="/pool-orders/new">Per Package</Menu.Item>
            </SubMenu>
            <SubMenu
              key="routes-menu"
              icon={
                <span className="anticon">
                  <RoutesIcon />
                </span>
              }
              title="Active Routes"
              onTitleClick={handleClickMenuSub}
            >
              <Menu.Item key="/routes">Active Routes</Menu.Item>
              <Menu.Item key="/plans">Plans</Menu.Item>
              <Menu.Item key="/saved-routes" disabled>
                Saved Routes
              </Menu.Item>
            </SubMenu>
            <Menu.Item
              key="/scan2pack"
              icon={
                <span className="anticon">
                  <AppstoreOutlined />
                </span>
              }
            >
              Scan 2 Pack
            </Menu.Item>
            {hasPoolOrders && (
              <>
                <Menu.Item
                  key="/pool"
                  icon={
                    <span className="anticon">
                      <GiftOutlined />
                    </span>
                  }
                >
                  Flexio Pool
                </Menu.Item>
              </>
            )}
            <Menu.Item
              key="/locations"
              icon={
                <span className="anticon">
                  <LocationsIcon />
                </span>
              }
            >
              Locations
            </Menu.Item>
            <Menu.Item
              key="/history"
              icon={
                <span className="anticon">
                  <HistoryIcon />
                </span>
              }
            >
              History
            </Menu.Item>
            <Menu.Item
              key="/client-users"
              icon={
                <span className="anticon">
                  <UserOutlined />
                </span>
              }
            >
              Users
            </Menu.Item>
            <Menu.Item
              key="/drivers"
              icon={
                <span className="anticon">
                  <DriversIcon />
                </span>
              }
            >
              My Drivers
            </Menu.Item>
            {(hasWarehouseAccess || hasPurchaseOrderAccess) && (
              <SubMenu
                key="inventory"
                icon={
                  <span className="anticon">
                    <AppstoreOutlined />
                  </span>
                }
                title="Inventory Management"
                onTitleClick={handleClickMenuSub}
              >
                {hasWarehouseAccess && (
                  <>
                    <Menu.Item key="/warehouses">Warehouses</Menu.Item>
                    <Menu.Item key="/warehouses">Inventory</Menu.Item>
                  </>
                )}
                {hasPurchaseOrderAccess && (
                  <Menu.Item key="/purchaseorders">Purchase Orders</Menu.Item>
                )}
              </SubMenu>
            )}
            <SubMenu
              key="billing"
              icon={
                <span className="anticon">
                  <InvoicingIcon />
                </span>
              }
              title="Billing"
              onTitleClick={handleClickMenuSub}
            >
              <Menu.Item key="/billing/dashboard">Dashboard</Menu.Item>
              <Menu.Item key="/billing/plans">Plans</Menu.Item>
              <Menu.Item key="/billing/upgrades">Upgrades</Menu.Item>
              <Menu.Item key="/billing/facturas">Invoices</Menu.Item>
              <Menu.Item key="/billing/subscriptions">Subscriptions</Menu.Item>
              <Menu.Item key="/billing/payment-method">Payment Methods</Menu.Item>
            </SubMenu>
            <SubMenu
              key="settings"
              icon={
                <span className="anticon">
                  <SettingIcon />
                </span>
              }
              title="Settings"
              onTitleClick={handleClickMenuSub}
            >
              <Menu.Item key="/settings/my-profile">My Profile</Menu.Item>
              <Menu.Item key="/api-key">Keys and IDs</Menu.Item>
              <Menu.Item key="/settings/notification">Notification Center</Menu.Item>
              <Menu.Item key="/settings/validations">Validations</Menu.Item>
              <Menu.Item key="/settings/route-options">Route Options</Menu.Item>
              <Menu.Item key="/settings/change-password">Change Password</Menu.Item>
              <Menu.Item key="/settings/company-settings">Company Settings</Menu.Item>
            </SubMenu>
            <Menu.Item
              key="/"
              icon={
                <span className="anticon">
                  <NeedHelpIcon />
                </span>
              }
            >
              <a href="https://support.goflexio.com">Support Page</a>
            </Menu.Item>
          </Menu>
        </div>
        <div className="sidebar-footer">
          <UserInfo
            isUser={true}
            user={profile}
            hideInfo={collapsed}
            setHideInfo={() => setCollapsed(false)}
            isAdmin={false}
          />
        </div>
        <div className="button-collapse" onClick={handleClickCollapse}>
          <span>{collapsed ? <ArrowRightIcon /> : <ArrowLeftIcon />}</span>
        </div>
      </Sider>
    </>
  );
};
export default ClientSiderContent;
